'use client'

import { useTheme } from '@/contexts/ThemeContext'

interface ThemeToggleProps {
  className?: string
  variant?: 'standalone' | 'inline' // standalone = icon button, inline = dropdown item
}

export default function ThemeToggle({ className = '', variant = 'standalone' }: ThemeToggleProps) {
  const { currentTheme, toggleThemeMode } = useTheme()
  const isDark = currentTheme.mode === 'dark'

  if (variant === 'inline') {
    // Inline version for dropdowns
    return (
      <button
        onClick={toggleThemeMode}
        className={`flex items-center w-full text-left transition-colors ${className}`}
        onMouseEnter={(e) => {
          if (e.currentTarget.classList.contains('hover-theme-primary')) {
            e.currentTarget.style.color = 'var(--theme-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget.classList.contains('hover-theme-primary')) {
            e.currentTarget.style.color = '';
          }
        }}
      >
        <span className="mr-3 text-xl">{isDark ? '☀️' : '🌙'}</span>
        <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
      </button>
    )
  }

  // Standalone icon button version
  return (
    <button
      onClick={toggleThemeMode}
      className={`flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors ${className}`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        <svg
          className="w-5 h-5 text-gray-700 dark:text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg
          className="w-5 h-5 text-gray-700 dark:text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  )
}
